import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { PlusIcon, CheckIcon, CogIcon } from '@heroicons/react/solid';
import ApiRequest from '../../api/ApiRequest';
import { API_AUTH_TYPES, API_ROUTES } from '../../api/apiConstants';
import { getColorClasses, getHexColor } from './constants';
import TaskTagManagementModal from './TaskTagManagementModal';

/**
 * TaskTagSelector - Searchable dropdown for assigning or removing tags on a task or goal.
 * Includes a link to the tag management modal.
 *
 * @param {Object} props
 * @param {string} [props.taskId] - Task ID (used when `type` is 'task').
 * @param {string} [props.goalId] - Goal ID (used when `type` is 'goal').
 * @param {'task'|'goal'} [props.type='task'] - Whether the selector is for a task or goal.
 * @param {Object[]} [props.selectedTags=[]] - Currently assigned tag objects.
 * @param {Function} [props.onTagsChange] - Called after a tag is assigned or removed.
 * @param {boolean} props.isOpen - Whether the dropdown is visible.
 * @param {Function} props.setIsOpen - Setter to open/close the dropdown.
 * @returns {JSX.Element}
 */
const TaskTagSelector = ({ taskId, goalId, type = 'task', selectedTags = [], onTagsChange, isOpen, setIsOpen }) => {
  const queryClient = useQueryClient();
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showManagement, setShowManagement] = useState(false);

  /** API client scoped to the task-tags routes for advisor access. */
  const tagsApi = new ApiRequest(API_ROUTES.TASK_TAGS, API_AUTH_TYPES.ADVISOR);

  // Fetch all available tags
  const { data: allTagsData } = useQuery({
    queryKey: ['task-tags'],
    queryFn: tagsApi.getFn({ endpoint: 'index' }),
    staleTime: 30000,
  });

  /** All tags available in the firm, normalised from the query result. */
  const allTags = allTagsData?.tags || [];

  /** Tags filtered by the current search term (case-insensitive). */
  const filteredTags = allTags.filter(tag =>
    tag.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  /**
   * Returns true when the given tag is already in `selectedTags`.
   * @param {string} tagId
   * @returns {boolean}
   */
  const isSelected = (tagId) => selectedTags.some(t => t.id === tagId);

  /** Assigns one or more tags to the current task or goal. */
  const { mutate: assignTag } = useMutation({
    mutationFn: ({ item_id, tag_ids }) => {
      if (type === 'goal') {
        return tagsApi.post({ endpoint: 'assign_goal', body: { goal_id: item_id, tag_ids } });
      }
      return tagsApi.post({ endpoint: 'assign', body: { task_id: item_id, tag_ids } });
    },
    onSuccess: () => {
      onTagsChange?.();
    },
  });

  /** Removes a single tag from the current task or goal. */
  const { mutate: removeTag } = useMutation({
    mutationFn: ({ item_id, tag_id }) => {
      if (type === 'goal') {
        return tagsApi.delete({ endpoint: 'remove_goal', params: [item_id, tag_id] });
      }
      return tagsApi.delete({ endpoint: 'remove', params: [item_id, tag_id] });
    },
    onSuccess: () => {
      onTagsChange?.();
    },
  });

  /**
   * Assigns the tag if it is not selected, or removes it if it is.
   * @param {{ id: string, name: string, color: string }} tag
   */
  const handleToggleTag = (tag) => {
    const itemId = type === 'goal' ? goalId : taskId;
    if (isSelected(tag.id)) {
      removeTag({ item_id: itemId, tag_id: tag.id });
    } else {
      assignTag({ item_id: itemId, tag_ids: [tag.id] });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    /**
     * Closes the dropdown when the user clicks outside the dropdown ref element.
     * @param {MouseEvent} event
     */
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  return (
    <>
      {/* Tag Management Modal - rendered outside dropdown so it persists */}
      <TaskTagManagementModal
        open={showManagement}
        setOpen={setShowManagement}
      />

      {/* Dropdown - only shown when isOpen */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
        >
      {/* Search input */}
      <div className="px-2 py-1.5">
        <input
          type="text"
          placeholder="Search tags..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          autoFocus
        />
      </div>

      {/* Tags list */}
      <div className="max-h-48 overflow-y-auto">
        {filteredTags.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">No tags found</div>
        ) : (
          filteredTags.map((tag) => {
            const colors = getColorClasses(tag.color);
            const hexColor = getHexColor(tag.color);
            const isCustom = colors.isCustom;
            const selected = isSelected(tag.id);

            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => handleToggleTag(tag)}
                className={`w-full px-3 py-1.5 text-sm flex items-center gap-2 hover:bg-gray-50 ${selected ? 'bg-gray-50' : ''}`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${!isCustom ? colors.dot : ''}`}
                  style={isCustom ? { backgroundColor: hexColor } : {}}
                />
                <span className="flex-1 text-left truncate">{tag.name}</span>
                {selected && <CheckIcon className="w-4 h-4 text-primary-600" />}
              </button>
            );
          })
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-200 my-1" />

      {/* Manage Tags link */}
      <button
        type="button"
        onClick={() => {
          setShowManagement(true);
          setIsOpen(false);
        }}
        className="w-full px-3 py-1.5 text-sm text-primary-600 hover:bg-gray-50 flex items-center gap-2"
      >
        <CogIcon className="w-4 h-4" />
        Manage Tags
      </button>
        </div>
      )}
    </>
  );
};

export default TaskTagSelector;
